import React, {Component} from 'react';
import {connect} from "react-redux";
import {withRouter} from "react-router-dom";
import BreadcrumbGroup from "@amzn/meridian/breadcrumb/breadcrumb-group";
import Breadcrumb from "@amzn/meridian/breadcrumb";
import Heading from "@amzn/meridian/heading";
import Column from "@amzn/meridian/column";
import Table from "@amzn/meridian/table";
import TableRow from "@amzn/meridian/table/table-row";
import TableCell from "@amzn/meridian/table/table-cell";
import {chainWalk, convertToString, getBaseUrl, getNodeId, hasAmxlViewFlagEnabled} from "../Utility";
import {fetchPackageListForFilters, fetchPickedPackageList} from "../actions/PackageListActions";
import Pagination from "@amzn/meridian/pagination";
import Row from "@amzn/meridian/row";
import {FormattedMessage} from "react-intl";
import {getLocale} from "../handler/LocaleHandler";
import {fetchCapability} from "../actions/OptionsActions";
import {Capability, PackageStatus} from "../enums";
import {HYPHEN, PACKAGE_STATUS} from "../constants";
import {getDocumentURL} from "../handler/ParentDataHandler";
import RefreshTimer from "./RefreshTimer";

class PackageList extends Component {

    constructor(props) {
        super(props);
        this.state = {
            currentPage: 1,
            sortColumn: "trackingId",
            sortDirection: "ascending",
            queryParams: new URLSearchParams(this.props.location.search)
        }
    }

    render() {
        const {packageList} = this.props;
        const queryParams = new URLSearchParams(this.props.location.search);
        const currentPage = this.state.currentPage;
        // Only apply the AMXL sideline column layout when this package list was
        // opened from the sideline view (SIDELINE / SIDELINE_V2). Other AMXL
        // package-list views (route, cluster, aisle, zone, picked) keep the default layout.
        const isSidelineView = queryParams.get("SIDELINE_V2") === "TRUE" || queryParams.get("SIDELINE") === "TRUE";
        const isAmxlSideline = hasAmxlViewFlagEnabled(getNodeId(), this.props.featureStatus) && isSidelineView;

        const onSort = (sortDetails) => {
            this.setState({
                sortColumn: sortDetails.sortColumn,
                sortDirection: sortDetails.sortDirection
            })

            switch (sortDetails.sortDirection) {
                case 'ascending':
                    packageList.sort((a, b) => customCompare(a, b));
                    break;
                case 'descending' :
                    packageList.sort((a, b) => customCompare(b, a));
                    break;
                default :
                    break;
            }

            function customCompare(a, b) {

                const orderedSizes = ["EXTRA_SMALL", "SMALL", "MEDIUM", "LARGE", "EXTRA_LARGE"];

                const fieldA = a[sortDetails.sortColumn];
                const fieldB = b[sortDetails.sortColumn];

                if (sortDetails.sortColumn === "size") {
                    return orderedSizes.indexOf(fieldA) - orderedSizes.indexOf(fieldB);
                }

                return convertToString(fieldA)
                    .localeCompare(convertToString(fieldB), getLocale(), {numeric: true});

            }

        }

        function isRouteCodeApplicable() {
            if (queryParams["PACKAGE_STATUS"] && (queryParams["PACKAGE_STATUS"] === "CURRENT_INDUCTED" || queryParams["PACKAGE_STATUS"] === "CURRENT_RECEIVED")) {
                return false;
            }
            return true;
        }

        const changePage = pageNumber => {
            this.setState(() => {
                return {
                    currentPage: pageNumber
                }
            })
        }

        return (
            <Column spacingInset={"large medium none large"}>
                <BreadcrumbGroup>
                    <Breadcrumb href={"#heatMap"}>
                        <FormattedMessage id="packageList.stow" defaultMessage="Stow"/>
                    </Breadcrumb>
                    <Breadcrumb>
                        <FormattedMessage id="packageList.packageList" defaultMessage="Package List"/>
                    </Breadcrumb>
                </BreadcrumbGroup>
                <Heading level={1} >
                    <FormattedMessage id="packageList.packageSummary" defaultMessage="Package Summary"/>
                </Heading>
                <RefreshTimer deps={[packageList]}/>
                <Table headerRows={1} spacing="small"
                       fixHeaderRows={true} fixHeaderRowsFullWidth={true}
                       sortColumn={this.state.sortColumn} sortDirection={this.state.sortDirection} onSort={onSort}>
                    <TableRow>
                        {isAmxlSideline ? this.renderAmxlSidelineHeadingCells() : <React.Fragment>
                        <TableCell sortColumn="trackingId">
                            <FormattedMessage id="packageList.trackingId" defaultMessage="Tracking Id"/>
                        </TableCell>
                        <TableCell sortColumn="lastScan">
                            <FormattedMessage id="packageList.lastScan" defaultMessage="Last scan"/>
                        </TableCell>
                        <TableCell sortColumn="minutesInState" alignmentHorizontal="right">
                            <FormattedMessage id="packageList.minute_in_state" defaultMessage="Minutes in state"/>
                        </TableCell>
                        <TableCell sortColumn="state">
                            <FormattedMessage id="packageList.state" defaultMessage="State"/>
                        </TableCell>
                        <TableCell sortColumn="reason">
                            <FormattedMessage id="packageList.reason" defaultMessage="Reason"/>
                        </TableCell>
                        {this.renderConveyanceHeading()}
                        <TableCell sortColumn="location">
                            <FormattedMessage id="packageList.location" defaultMessage="Location"/>
                        </TableCell>
                        <TableCell sortColumn="associateId">
                            <FormattedMessage id="packageList.associate_id" defaultMessage="Associate ID"/>
                        </TableCell>
                        {this.renderCycleHeading()}
                        {this.renderSortZoneHeading()}
                        {this.renderClusterHeading()}
                        {this.renderCartAndBinHeading()}
                        <TableCell sortColumn="ead">
                            <FormattedMessage id="packageList.ead" defaultMessage="EAD"/>
                        </TableCell>
                        <TableCell sortColumn="size">
                            <FormattedMessage id="packageList.size" defaultMessage="Size"/>
                        </TableCell>
                        {isRouteCodeApplicable() ?
                            <TableCell sortColumn="routeCode">
                                <FormattedMessage id="packageList.routeCode" defaultMessage="Route Code"/>
                            </TableCell> : null}
                        </React.Fragment>}
                    </TableRow>
                    {
                        packageList && packageList.slice((currentPage - 1) * 25, currentPage * 25).map(packageObject => (
                            <TableRow key={packageObject.trackingId}>
                                {isAmxlSideline ? this.renderAmxlSidelineRowCells(packageObject) : <React.Fragment>
                                <TableCell>
                                    {chainWalk(() => packageObject.trackingId, HYPHEN)}
                                </TableCell>
                                <TableCell>
                                    {chainWalk(() => packageObject.lastScan, HYPHEN)}
                                </TableCell>
                                <TableCell alignmentHorizontal="right">
                                    {chainWalk(() => packageObject.minutesInState, HYPHEN)}
                                </TableCell>
                                <TableCell>
                                    {chainWalk(() => packageObject.state, HYPHEN)}
                                </TableCell>
                                <TableCell>
                                    {chainWalk(() => packageObject.reason, HYPHEN)}
                                </TableCell>
                                {this.renderConveyanceValue(packageObject)}
                                {this.renderLocationValue(packageObject)}
                                <TableCell>
                                    {chainWalk(() => packageObject.associateId, HYPHEN)}
                                </TableCell>
                                {this.renderCycleValue(packageObject)}
                                {this.renderSortZoneValue(packageObject)}
                                {this.renderClusterValue(packageObject)}
                                {this.renderCartAndBinValue(packageObject)}
                                <TableCell>
                                    {chainWalk(() => packageObject.ead, HYPHEN)}
                                </TableCell>
                                <TableCell>
                                    {chainWalk(() => packageObject.size, HYPHEN)}
                                </TableCell>
                                {isRouteCodeApplicable() ?
                                    <TableCell>
                                        {chainWalk(() => packageObject.routeCode, HYPHEN)}
                                    </TableCell> : null}
                                </React.Fragment>}
                            </TableRow>
                        ))
                    }
                </Table>
                <Row alignmentVertical="top" alignmentHorizontal="center">
                    <Pagination numberOfPages={Math.ceil(packageList.length / 25)}
                                onChange={pageNumber => changePage(pageNumber)}
                                currentPage={this.state.currentPage}
                    />
                </Row>
            </Column>
        );
    }

    renderAmxlSidelineHeadingCells = () => {
        return (
            <React.Fragment>
                <TableCell sortColumn="trackingId">
                    <FormattedMessage id="packageList.trackingId" defaultMessage="Tracking Id"/>
                </TableCell>
                <TableCell sortColumn="lastScan">
                    <FormattedMessage id="packageList.lastScan" defaultMessage="Last scan"/>
                </TableCell>
                <TableCell sortColumn="associateId">
                    <FormattedMessage id="packageList.associate_id" defaultMessage="Associate ID"/>
                </TableCell>
                <TableCell sortColumn="sortZone">
                    <FormattedMessage id="packageList.sortZone" defaultMessage="Sort Zone"/>
                </TableCell>
                <TableCell sortColumn="scheduledDeliveryStart">
                    <FormattedMessage id="packageList.scheduledDeliveryStart" defaultMessage="Scheduled Delivery Start"/>
                </TableCell>
                <TableCell sortColumn="scheduledDeliveryEnd">
                    <FormattedMessage id="packageList.scheduledDeliveryEnd" defaultMessage="Scheduled Delivery End"/>
                </TableCell>
                <TableCell sortColumn="pdd">
                    <FormattedMessage id="packageList.pdd" defaultMessage="PDD"/>
                </TableCell>
                <TableCell sortColumn="routeCode">
                    <FormattedMessage id="packageList.routeCode" defaultMessage="Route Code"/>
                </TableCell>
                <TableCell sortColumn="dispatchTime">
                    <FormattedMessage id="packageList.dispatchTime" defaultMessage="Dispatch Time"/>
                </TableCell>
                <TableCell sortColumn="shipOption">
                    <FormattedMessage id="packageList.shipOption" defaultMessage="Ship Option"/>
                </TableCell>
                <TableCell sortColumn="state">
                    <FormattedMessage id="packageList.state" defaultMessage="State"/>
                </TableCell>
                <TableCell sortColumn="reason">
                    <FormattedMessage id="packageList.reason" defaultMessage="Reason"/>
                </TableCell>
                <TableCell sortColumn="minutesInState" alignmentHorizontal="right">
                    <FormattedMessage id="packageList.minute_in_state" defaultMessage="Minutes in state"/>
                </TableCell>
                <TableCell sortColumn="cycle">
                    <FormattedMessage id="packageList.cycle" defaultMessage="Cycle"/>
                </TableCell>
                <TableCell sortColumn="location">
                    <FormattedMessage id="packageList.location" defaultMessage="Location"/>
                </TableCell>
            </React.Fragment>
        )
    }

    renderAmxlSidelineRowCells = (packageObject) => {
        return (
            <React.Fragment>
                <TableCell>{chainWalk(() => packageObject.trackingId, HYPHEN)}</TableCell>
                <TableCell>{chainWalk(() => packageObject.lastScan, HYPHEN)}</TableCell>
                <TableCell>{chainWalk(() => packageObject.associateId, HYPHEN)}</TableCell>
                <TableCell>{chainWalk(() => packageObject.sortZone, HYPHEN)}</TableCell>
                <TableCell>{chainWalk(() => packageObject.scheduledDeliveryStart, HYPHEN)}</TableCell>
                <TableCell>{chainWalk(() => packageObject.scheduledDeliveryEnd, HYPHEN)}</TableCell>
                <TableCell>{chainWalk(() => packageObject.pdd, HYPHEN)}</TableCell>
                <TableCell>{chainWalk(() => packageObject.routeCode, HYPHEN)}</TableCell>
                <TableCell>{chainWalk(() => packageObject.dispatchTime, HYPHEN)}</TableCell>
                <TableCell>{chainWalk(() => packageObject.shipOption, HYPHEN)}</TableCell>
                <TableCell>{chainWalk(() => packageObject.state, HYPHEN)}</TableCell>
                <TableCell>{chainWalk(() => packageObject.reason, HYPHEN)}</TableCell>
                <TableCell alignmentHorizontal="right">{chainWalk(() => packageObject.minutesInState, HYPHEN)}</TableCell>
                <TableCell>{chainWalk(() => packageObject.cycle, HYPHEN)}</TableCell>
                <TableCell>{chainWalk(() => packageObject.location, HYPHEN)}</TableCell>
            </React.Fragment>
        )
    }

    renderConveyanceHeading = () => {
        return Capability.AR_NEXT_GEN === this.props.capability && (
            <TableCell sortColumn="station_pair">
                <FormattedMessage id="packageList.station_pair" defaultMessage="Station"/>
            </TableCell>
        )
    }

    renderCycleHeading = () => {
        return Capability.AMZL === this.props.capability && (
            <TableCell sortColumn="cycle">
                <FormattedMessage id="packageList.cycle" defaultMessage="Cycle"/>
            </TableCell>
        )
    }

    renderSortZoneHeading = () => {
        return Capability.AMZL === this.props.capability && (
            <TableCell sortColumn="sortZone">
                <FormattedMessage id="packageList.sortZone" defaultMessage="Sort Zone"/>
            </TableCell>
        )
    }

    renderClusterHeading = () => {
        return Capability.AMZL === this.props.capability && (
            <TableCell sortColumn="cluster">
                <FormattedMessage id="packageList.cluster" defaultMessage="Cluster"/>
            </TableCell>
        )
    }

    renderCartAndBinHeading = () => {
        return this.isArNextGenStowedStatus() && (
            <TableCell sortColumn="cartAndBin">
                <FormattedMessage id="packageList.cartAndBin" defaultMessage="Cart/Bin"/>
            </TableCell>
        )
    }

    renderConveyanceValue = (packageObject) => {
        return Capability.AR_NEXT_GEN === this.props.capability && (
            <TableCell> {chainWalk(() => packageObject.conveyance, HYPHEN)} </TableCell>
        )
    }

    renderLocationValue = (packageObject) => {
        if (this.isArNextGenStowedStatus()) {
            const conveyance = chainWalk(() => packageObject.conveyance, HYPHEN)
            const floorSuffix = chainWalk(() => packageObject.floor.split(".")[1], HYPHEN)
            const value = conveyance + ", " + floorSuffix
            return <TableCell> {value} </TableCell>
        } else {
            return <TableCell> {chainWalk(() => packageObject.location, HYPHEN)} </TableCell>
        }
    }

    renderCycleValue = (packageObject) => {
        return Capability.AMZL === this.props.capability && (
            <TableCell> {chainWalk(() => packageObject.cycle, HYPHEN)} </TableCell>
        )
    }

    renderSortZoneValue = (packageObject) => {
        return Capability.AMZL === this.props.capability && (
            <TableCell> {chainWalk(() => packageObject.sortZone, HYPHEN)} </TableCell>
        )
    }

    renderClusterValue = (packageObject) => {
        return Capability.AMZL === this.props.capability && (
            <TableCell> {chainWalk(() => packageObject.cluster, HYPHEN)} </TableCell>
        )
    }

    renderCartAndBinValue = (packageObject) => {
        if (this.isArNextGenStowedStatus()) {
            const cart = chainWalk(() => packageObject.location.split(".")[0], HYPHEN)
            const binSuffix = chainWalk(() => packageObject.location.split(".")[1], null)
            const value = binSuffix ? cart + ", " + binSuffix : cart
            return <TableCell> {value} </TableCell>
        }
    }

    isArNextGenStowedStatus = () => {
        return Capability.AR_NEXT_GEN === this.props.capability && PackageStatus.CURRENT_STOWED === this.state.queryParams.get(PACKAGE_STATUS)
    }

    componentDidMount() {
        this.props.dispatch(fetchCapability()).then(() => {
            const nodeId = getNodeId()
            const queryParams = this.state.queryParams
            if (PackageStatus.PICKED === queryParams.get(PACKAGE_STATUS)) {
                getDocumentURL().then(value => {
                    const baseURL = getBaseUrl(value)
                    this.props.dispatch(fetchPickedPackageList(baseURL, nodeId, queryParams))
                });
            } else {
                this.props.dispatch(fetchPackageListForFilters(nodeId, queryParams, this.props.capability));
            }
        })
    }

}


const mapStateToProps = state => ({
    capability: state.optionsReducer.capability,
    packageList: state.packageListReducer.packageList,
    featureStatus: state.configReducer.featureStatus,
});
export default withRouter(connect(mapStateToProps)(PackageList));



// WEBPACK FOOTER //
// ./src/components/PackageList.js